import { FC, memo } from 'react';
import { useUpdateProjectMutation } from 'store/features/project/project-api';
import { useQueryError } from 'lib/hooks/useQueryError';
import { useTranslation } from 'react-i18next';
import { Project } from 'types/project';
import { ProjectForm, ProjectFormSubmitData } from '../ProjectForm/ProjectForm';
import { Loader } from 'components/common/Loader/Loader';
import { ErrorModal } from 'components/common/ErrorModal/ErrorModal';

type ProjectEditorProps = {
  projectId: Project['id'];
  name: Project['name'];
  instruction: Project['instruction'];
  files: Project['files'];
  onClose: () => void;
};

export const ProjectEditor: FC<ProjectEditorProps> = memo(
  ({ projectId, name, instruction, files, onClose }) => {
    const { t } = useTranslation();

    const [
      updateProject,
      {
        isLoading: isUpdateLoading,
        isError: isUpdateError,
        error: updateError,
      },
    ] = useUpdateProjectMutation();

    const { showError, closeError, errorMessage } = useQueryError(
      isUpdateError,
      updateError,
    );

    const handleUpdate = async ({
      name: updatedName,
      instruction: updatedInstruction,
      newFiles,
      removedFileIds,
    }: ProjectFormSubmitData) => {
      await updateProject({
        projectId,
        name: updatedName,
        instruction: updatedInstruction,
        newFiles,
        removedFileIds,
      }).unwrap();

      onClose();
    };

    return (
      <>
        <ProjectForm
          title={t('project.edit')}
          textSubmit={t('common.apply')}
          name={name}
          instruction={instruction}
          files={files}
          onClose={onClose}
          onSubmit={handleUpdate}
        />

        {isUpdateLoading && <Loader />}
        {showError && (
          <ErrorModal message={errorMessage} onClose={closeError} />
        )}
      </>
    );
  },
);

ProjectEditor.displayName = 'ProjectEditor';
